import React, { useContext, useEffect, useState } from "react";
import { EditableCheckbox } from "../../Atoms/inputs/EditableCheckbox";
import { EditableFormAttributeValueProperty } from "../../Atoms/EditableProperties/EditableFormAttributeValueProperty";
import { WindowContext } from "../../Service/Context/WindowContext";
import { DocumentService } from "../../Service/DocumentService";
import { FormatS } from "../../Service/FormatS";
import { Text } from "@mantine/core";
import { isDefined, printify } from "../../Utils/utils";
import { getDocumentTranslator } from "../Document/documentLanguage";
import { useLingui } from "@lingui/react";
import { patchDocumentPosition } from "../../Service/restapi/sharedContractService";
import { useToasts } from "react-toast-notifications";

// eslint-disable-next-line import/no-anonymous-default-export
export default ({
  _document,
  formSubmission,
  position,
  taxType,
  values,
  handleChange,
  columnStyles,
  onPositionChange,
}) => {
  const { windowWidth } = useContext(WindowContext);
  const { addToast } = useToasts();
  const { currency } = _document;
  const { i18n } = useLingui();
  const t = getDocumentTranslator(i18n, _document.language);
  const { discount, priceUnit, price, netPrice, tax } = position;
  const { amount: discountAmount } = discount;
  const minAmount = getMinAmount(position.min);
  const maxAmount = getMaxAmount(position.max);
  const [amount, setAmount] = useState(getInitialAmount(position.amount, minAmount, maxAmount));
  const [amountLoading, setAmountLoading] = useState(false);
  const [amountError, setAmountError] = useState(false);

  useEffect(() => {
    setAmount(getInitialAmount(position.amount, minAmount, maxAmount));
  }, [position.amount, minAmount, maxAmount]);

  let styles = {
    container: {
      marginBottom: 5,
      display: "flex",
    },
    label: {
      fontWeight: 600,
      overflowWrap: "break-word",
      wordWrap: "break-word",
    },
    subtext: {},
    firstColumn: {
      ...columnStyles.firstColumn,
    },
    textColumn: {
      ...columnStyles.textColumn,
    },
    priceColumn: {
      ...columnStyles.priceColumn,
    },
    amountInput: {
      width: 74,
      maxWidth: 74,
      marginLeft: "auto",
    },
    amountInputWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
    },
  };

  let patchField = position.customAttributeId
    ? position.customAttributeId
    : position.id;
  let keys = [];
  let obj;

  for (obj in values) {
    if (values[obj]) {
      keys.push(obj);
    }
  }

  const commitAmount = (newAmount) => {
    const requestedAmount = parseInt(newAmount, 10);
    const nextAmount = clampAmount(newAmount, minAmount, maxAmount);

    setAmount(nextAmount);

    if (isDefined(maxAmount) && requestedAmount > maxAmount) {
      addToast(`Die angefragte Menge übersteigt die verfügbare Kapazität. Für diese Position können maximal ${maxAmount} bereitgestellt werden.`, {
        appearance: "info",
        autoDismiss: true,
      });
    }

    if (`${nextAmount}` === `${position.amount}` || !_document || !_document.id) {
      return;
    }

    setAmountLoading(true);
    setAmountError(false);

    patchDocumentPosition(_document.id, position.id, {
      amount: `${nextAmount}`,
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
        throw resp;
      })
      .then((updatedPosition) => {
        if (typeof onPositionChange === "function") {
          onPositionChange(updatedPosition);
        }
      })
      .catch((error) => {
        setAmountError(true);
        console.error("could not patch document position amount", error);
      })
      .finally(() => setAmountLoading(false));
  };

  const renderAmount = () => {
    if (!position.negotiable) {
      return (
        <>
          {position.amount} {position.unit}
        </>
      );
    }

    return (
      <div style={styles.amountInputWrapper}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={amount}
          disabled={formSubmission.lastSubmitted}
          onChange={(event) => setAmount(event.currentTarget.value)}
          onBlur={(event) => commitAmount(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
          style={{
            ...styles.amountInput,
            textAlign: "left",
            height: 28,
            minHeight: 28,
            paddingLeft: 8,
            paddingRight: 4,
            borderColor: amountError ? "red" : undefined,
          }}
          className={`form-control${amountLoading ? " loading" : ""}`}
        />
        {isDefined(position.unit) && (
          <Text size="sm">{printify(position.unit)}</Text>
        )}
      </div>
    );
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.firstColumn}>
          {position.optional && (
            <EditableFormAttributeValueProperty
              formSubmissionId={formSubmission.id}
              patchField={patchField}
              changeHandler={(p) => {
                handleChange(patchField, p[patchField]);
              }}
              entity={values}
              values={keys}
              labelValue={position.id + ""}
              editFormatter={EditableCheckbox}
              multiple={false}
              editOnly={true}
              disabled={formSubmission.lastSubmitted}
            />
          )}
        </div>
        <div style={styles.textColumn}>
          <div style={styles.label}>
            <div dangerouslySetInnerHTML={{ __html: position.label }} />
          </div>
          <div
            className={"description"}
            style={styles.subtext}
            dangerouslySetInnerHTML={{ __html: position.subtext }}
          />
        </div>
        {windowWidth > 800 && (
          <div style={styles.priceColumn}>
            {renderAmount()}
          </div>
        )}
        {windowWidth > 800 && (
          <div style={styles.priceColumn}>
            {priceUnit && (
              <div>
                {FormatS.formatPriceWithCurrency(
                  currency,
                  DocumentService.calculateNetGrossPrice(
                    taxType,
                    priceUnit,
                    tax
                  )
                )}
              </div>
            )}
          </div>
        )}
        <div style={styles.priceColumn}>
          <div>
            {windowWidth < 800 &&
              (position.negotiable ? (
                renderAmount()
              ) : (
                !(isAmountOne(position.amount) && !isDefined(position.unit)) && (
                  <Text size="xs" color="dimmed">{`${position.amount} ${printify(
                    position.unit
                  )}`}</Text>
                )
              ))}
            <b>
              {FormatS.formatPriceWithCurrency(
                currency,
                taxType === "net" ? netPrice : price
              )}
            </b>
          </div>
          {discountAmount > 0 && (
            <Text size="xs">{`${t("discount")}: ${FormatS.formatPriceWithCurrency(
              currency,
              discountAmount
            )}`}</Text>
          )}
        </div>
      </div>
    </>
  );
};

const getMinAmount = (min) => {
  const parsedMin = parseInt(min, 10);
  return parsedMin > 0 ? parsedMin : 1;
};

const getMaxAmount = (max) => {
  const parsedMax = parseInt(max, 10);
  return parsedMax > 0 ? parsedMax : undefined;
};

const clampAmount = (amount, minAmount, maxAmount) => {
  let parsedAmount = parseInt(amount, 10);

  if (Number.isNaN(parsedAmount)) {
    parsedAmount = minAmount;
  }

  parsedAmount = Math.max(parsedAmount, minAmount);

  if (isDefined(maxAmount)) {
    parsedAmount = Math.min(parsedAmount, maxAmount);
  }

  return parsedAmount;
};

const getInitialAmount = (amount, minAmount, maxAmount) => {
  return clampAmount(amount, minAmount, maxAmount);
};

const isAmountOne = (amount) => {
  return parseFloat(amount) === 1;
};
